import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, ArrowLeft } from 'lucide-react';

function ResetPassword(props) {
    const [email, setEmail] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        document.title = 'Reset Password Page';
        
        // Add theme class based on domain
        const domain = window.location.host.split('.').slice(-2).join('.').toLowerCase();
        if (domain === "rxhacker.com" || domain === "localhost:5464" || 
            window.location.host.toLowerCase() === "tempfilter.23byt.es" || 
            window.location.host.toLowerCase() === "reporting.nulifespanrx.com") {
            document.body.classList.add('rx-user');
        }
    }, []);

    const getLogoUrl = () => {
        const host = window.location.host.toLowerCase();
        const domain = window.location.host.split('.').slice(-2).join('.').toLowerCase();
        
        if (host === "reporting.patientrxdiscount.com") {
            return '/Patient-Rx-Discount.svg';
        } else if (domain === "rxhacker.com" || domain === "localhost:5464" || 
                   host === "tempfilter.23byt.es" || host === "reporting.nulifespanrx.com") {
            return '/Rxhacker_logo_new.png';
        } else if (domain === "glichealth.com") {
            return '/GlicHealth_logo.png';
        } else if (domain === "honestdiscounts.com") {
            return '/hdrx-logo.svg';
        }
        return '/GlicHealth_logo.png';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSubmitted(true);

        if (!email || email.trim() === '') {
            return;
        }

        setLoading(true);

        try {
            const requestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    UserEmail: email, 
                    RequestURL: window.location.origin 
                })
            };

            const response = await fetch('/api/Account/requestpwdreset', requestOptions);
            const data = await response.json();

            if (data && data.length > 0) {
                if (data[0].status_code === "1") {
                    window.location.href = window.location.origin + '/passwordChanged';
                } else {
                    setError(data[0].status_message || 'Please try again later.');
                }
            } else {
                setError('Please try again later.');
            }
        } catch (err) {
            setError('Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
            <Card className="w-full max-w-md shadow-lg border bg-card">
                <CardContent className="p-6 lg:py-8 lg:px-8">
                    <div className="flex justify-center mb-4">
                        <img 
                            src={window.location.origin + getLogoUrl()} 
                            alt="logo" 
                            className="w-[190px] object-contain"
                            crossOrigin="anonymous"
                        />
                    </div>

                    <h1 className="text-2xl font-semibold text-center text-slate-900 mb-6">
                        Reset Password
                    </h1>

                    <p className="text-sm text-slate-600 text-center mb-6">
                        Enter your email address and we'll send you a link to reset your password.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Input
                                type="email"
                                placeholder="you@example.com"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setError('');
                                }}
                                className={`${
                                    (submitted && !email) || error 
                                        ? 'border-red-500' 
                                        : ''
                                }`}
                            />
                            {submitted && !email && (
                                <p className="text-sm text-red-500">Email address is required</p>
                            )}
                        </div>

                        {error && (
                            <p className="text-sm text-red-500 text-center">{error}</p>
                        )}

                        <Button 
                            type="submit" 
                            className="w-full font-semibold bg-primary hover:bg-primary/90 text-primary-foreground"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                'Reset Password'
                            )}
                        </Button>

                        <div className="text-center">
                            <NavLink 
                                to="/login" 
                                className="inline-flex items-center text-sm font-medium text-slate-700 hover:text-slate-900 hover:underline"
                            >
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                Back to Login
                            </NavLink>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}

export default ResetPassword;

